import { useContext, useEffect, useRef, useState, useCallback } from "react";
import { Button, Loading, Toolbar, useNotify, useTranslate } from "react-admin";
import { Box, Dialog, Grid } from "@mui/material";
import { FilePreview } from "./FilePreview";
import { ArrivalForm } from "./ArrivalForm";
import { ProductListForm } from "./ProductListForm";
import { FileManagerContext } from "./FileManagerContext";
import { getMediaExtraction, startMediaExtraction } from "shared/src/utils/mediaExtraction";
import { handleHttpError } from "shared/src/utils/errorHandler";

export const FileInformationManager = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const [loading, setLoading] = useState(false);
    const [extractionStatus, setExtractionStatus] = useState();
    const pollTimeoutRef = useRef();
    const {
        setData,
        page,
        submit,
        media,
        isUploading,
        nextPage,
        previousPage,
        handleClose,
        matchData,
    } = useContext(FileManagerContext);

    const extractData = useCallback(
        async ({ force = false, poll = false } = {}) => {
            if (pollTimeoutRef.current) {
                clearTimeout(pollTimeoutRef.current);
            }
            if (!media) return;
            setLoading(true);
            try {
                const data = poll
                    ? await getMediaExtraction(media._links.extractData)
                    : await startMediaExtraction(media._links.extractData, { force });
                setExtractionStatus(data.status);
                if (data.status === "failed") {
                    const message = translate("equip.extraction.extractionError");
                    throw new Error(data.errorId ? `${message} (${data.errorId})` : message);
                }
                if (!data.extracted_data) {
                    pollTimeoutRef.current = setTimeout(() => extractData({ poll: true }), 5000);
                    return;
                }
                const matchedData = matchData(data.extracted_data);
                setData(matchedData);
                setExtractionStatus();
                setLoading(false);
            } catch (error) {
                handleHttpError(error, notify);
                setLoading(false);
            }
        },
        [setData, matchData, notify, media, translate]
    );

    useEffect(() => {
        if (!media) return;
        const loadData = async () => {
            await extractData();
        };
        loadData();
    }, [extractData, media]);

    useEffect(() => {
        if (media) return;
        if (pollTimeoutRef.current) {
            clearTimeout(pollTimeoutRef.current);
        }
        setExtractionStatus();
    }, [media]);

    useEffect(() => {
        return () => {
            if (pollTimeoutRef.current) {
                clearTimeout(pollTimeoutRef.current);
            }
        };
    }, []);

    const loadingSecondary =
        extractionStatus && ["queued", "processing"].includes(extractionStatus)
            ? translate(`equip.extraction.${extractionStatus}`)
            : translate("equip.extraction.extracting");

    return (
        <Dialog open={!!media} fullScreen>
            <Box sx={{ px: 2 }}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                        <FilePreview media={media} />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                        {isUploading && (
                            <Loading
                                loadingPrimary={`${translate("loading")}...`}
                                loadingSecondary={`${translate("equip.extraction.uploading")}`}
                            />
                        )}
                        {loading && (
                            <Loading
                                loadingPrimary={`${translate("loading")}...`}
                                loadingSecondary={loadingSecondary}
                            />
                        )}
                        {!loading && !isUploading && page === 1 && <ArrivalForm />}
                        {!loading && !isUploading && page === 2 && <ProductListForm />}
                    </Grid>
                </Grid>
                <Toolbar
                    sx={{
                        justifyContent: "space-between",
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        width: "100%",
                    }}
                >
                    <Button variant="outlined" color="primary" onClick={handleClose}>
                        {translate("ra.action.cancel")}
                    </Button>
                    {!loading && !isUploading && (
                        <Button onClick={() => extractData({ force: true })}>
                            {translate("equip.reExtract")}
                        </Button>
                    )}
                    {page === 1 && !loading && !isUploading && (
                        <Button resource="skus" variant="contained" onClick={nextPage}>
                            {translate("equip.next")}
                        </Button>
                    )}
                    {page === 2 && !loading && !isUploading && (
                        <Box sx={{ display: "flex", gap: 2 }}>
                            <Button resource="skus" variant="contained" onClick={previousPage}>
                                {translate("equip.back")}
                            </Button>
                            <Button resource="skus" variant="contained" onClick={submit}>
                                {translate("equip.confirm")}
                            </Button>
                        </Box>
                    )}
                </Toolbar>
            </Box>
        </Dialog>
    );
};
